import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useJWTAuth } from '../context/JWTAuthContext';
import travelApiService from '../utils/travelApiService';
import './SharePreviewPage.css';

const SharePreviewPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useJWTAuth();

  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAccepting, setIsAccepting] = useState(false);

  const permissionLabels = {
    view: { title: '읽기 전용', icon: '👁️', color: '#3b82f6' },
    edit: { title: '편집 가능', icon: '✏️', color: '#10b981' },
    admin: { title: '관리자', icon: '⚙️', color: '#f59e0b' },
  };

  useEffect(() => {
    fetchTokenInfo();
  }, [token]);

  const fetchTokenInfo = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await travelApiService.getShareLinkInfo(token);

      if (response.success && response.data) {
        setTokenInfo(response.data);
      } else {
        throw new Error(response.message || '공유 링크 정보를 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('공유 링크 정보 조회 오류:', error);

      if (error.response?.status === 404) {
        setError({
          title: '존재하지 않는 링크',
          message: '삭제되었거나 잘못된 공유 링크입니다.',
          icon: '🔍',
        });
      } else if (error.response?.status === 410) {
        const errorCode = error.response.data?.error?.code;

        if (errorCode === 'TOKEN_EXPIRED') {
          setError({
            title: '만료된 링크',
            message: '이 공유 링크는 만료되었습니다. 링크를 공유한 사람에게 새 링크를 요청하세요.',
            icon: '⏰',
          });
        } else if (errorCode === 'TOKEN_MAX_USES') {
          setError({
            title: '사용 횟수 초과',
            message: '이 공유 링크는 최대 사용 횟수에 도달했습니다.',
            icon: '🚫',
          });
        } else {
          setError({
            title: '사용할 수 없는 링크',
            message: error.response.data?.error?.details || '이 공유 링크는 더 이상 사용할 수 없습니다.',
            icon: '❌',
          });
        }
      } else {
        setError({
          title: '오류 발생',
          message: error.message || '공유 링크 정보를 불러오는 중 오류가 발생했습니다.',
          icon: '❌',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      // 로그인 페이지로 이동하되, 돌아올 경로 저장
      navigate('/login', { state: { returnTo: location.pathname } });
      return;
    }

    setIsAccepting(true);

    try {
      const response = await travelApiService.acceptShareLink(token);

      if (response.success && response.data) {
        alert('✅ 여행 계획이 공유되었습니다!');
        // 여행 계획 상세 페이지로 이동
        navigate(`/travel/${response.data.id}`);
      } else {
        throw new Error(response.message || '공유 수락에 실패했습니다');
      }
    } catch (error) {
      console.error('공유 수락 오류:', error);

      if (error.response?.status === 400) {
        alert('본인의 여행 계획입니다. 이미 접근 가능합니다.');
        // 여행 계획 목록으로 이동
        navigate('/travel');
      } else if (error.response?.status === 409) {
        alert('이미 공유된 여행 계획입니다.');
        navigate('/travel');
      } else if (error.response?.status === 401) {
        alert('로그인이 필요합니다.');
        navigate('/login', { state: { returnTo: location.pathname } });
      } else {
        alert(`공유 수락 실패: ${error.message || '알 수 없는 오류'}`);
      }
    } finally {
      setIsAccepting(false);
    }
  };

  const handleLogin = () => {
    navigate('/login', { state: { returnTo: location.pathname } });
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return '만료 없음';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeRemaining = (dateString) => {
    if (!dateString) return null;

    const expiry = new Date(dateString);
    const now = new Date();
    const diff = expiry - now;

    if (diff <= 0) return '만료됨';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days}일 ${hours}시간 남음`;
    } else if (hours > 0) {
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours}시간 ${minutes}분 남음`;
    } else {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes}분 남음`;
    }
  };

  if (loading) {
    return (
      <div className="share-preview-page">
        <div className="share-preview-container">
          <div className="loading-spinner"></div>
          <p>공유 링크 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="share-preview-page">
        <div className="share-preview-container">
          <div className="error-card">
            <div className="error-icon">{error.icon}</div>
            <h2>{error.title}</h2>
            <p>{error.message}</p>
            <button className="btn-primary" onClick={() => navigate('/travel')}>
              여행 계획 목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  const permission = permissionLabels[tokenInfo.permission];
  const timeRemaining = getTimeRemaining(tokenInfo.expires_at);

  return (
    <div className="share-preview-page">
      <div className="share-preview-container">
        <div className="share-preview-card">
          <div className="preview-header">
            <div className="preview-icon">🎉</div>
            <h1>여행 계획 공유 초대</h1>
            <p className="preview-subtitle">친구가 여행 계획을 공유했습니다!</p>
          </div>

          <div className="preview-content">
            <div className="preview-info-card">
              <h3>📋 공유 정보</h3>

              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">권한</span>
                  <span className="info-badge" style={{ background: permission.color }}>
                    {permission.icon} {permission.title}
                  </span>
                </div>

                <div className="info-item">
                  <span className="info-label">만료</span>
                  <span className="info-value">
                    {formatExpiryDate(tokenInfo.expires_at)}
                    {timeRemaining && timeRemaining !== '만료 없음' && (
                      <span className="time-remaining"> ({timeRemaining})</span>
                    )}
                  </span>
                </div>

                <div className="info-item">
                  <span className="info-label">사용 현황</span>
                  <span className="info-value">
                    {tokenInfo.used_count} / {tokenInfo.max_uses || '∞'}
                  </span>
                </div>

                {tokenInfo.max_uses && (
                  <div className="info-item">
                    <span className="info-label">남은 사용 횟수</span>
                    <span className="info-value highlight">
                      {tokenInfo.max_uses - tokenInfo.used_count}회
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="permission-description-card">
              <h4>{permission.icon} {permission.title} 권한</h4>
              <p>
                {permission.title === '읽기 전용' &&
                  '여행 계획을 볼 수 있습니다. 수정은 할 수 없습니다.'}
                {permission.title === '편집 가능' &&
                  '여행 계획을 보고 수정할 수 있습니다.'}
                {permission.title === '관리자' &&
                  '여행 계획의 모든 것을 관리할 수 있습니다.'}
              </p>
            </div>
          </div>

          <div className="preview-actions">
            {isAuthenticated ? (
              <button
                className="btn-accept"
                onClick={handleAccept}
                disabled={isAccepting}
              >
                {isAccepting ? '수락 중...' : '✅ 공유 수락하기'}
              </button>
            ) : (
              <div className="login-required">
                <p className="login-message">로그인 후 공유를 수락할 수 있습니다</p>
                <button className="btn-login" onClick={handleLogin}>
                  🔐 로그인하기
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePreviewPage;
